/**
 * Preferences popover - theme, density, accent, reduced motion.
 *
 * Triggered from the app shell (avatar dropdown or a direct button) and
 * also via a global keyboard shortcut: `Cmd+,` on Mac / `Ctrl+,` on
 * Windows/Linux - matches the OS-standard "Preferences" binding.
 *
 * Escape closes. Click outside closes. Uses inert focus-trap-ish behavior
 * via autofocus on first control.
 */

import { Monitor, Moon, Settings2, Sun, X, Zap } from "lucide-react";
import { useEffect, useRef } from "react";

import { usePreferences } from "@/components/preferences-context";
import { ACCENT_PRESETS, type Density, type Theme } from "@/lib/preferences";
import { cn } from "@/lib/utils";

export function PreferencesPopover(props: {
  open: boolean;
  onClose: () => void;
}): JSX.Element | null {
  const { preferences, setPreferences, resetPreferences } = usePreferences();
  const popRef = useRef<HTMLDivElement>(null);

  // Close on Escape + click-outside.
  useEffect(() => {
    if (!props.open) return;
    const onKey = (e: KeyboardEvent): void => {
      if (e.key === "Escape") props.onClose();
    };
    const onClick = (e: MouseEvent): void => {
      if (popRef.current && !popRef.current.contains(e.target as Node)) {
        props.onClose();
      }
    };
    document.addEventListener("keydown", onKey);
    // Delay the click listener a tick so the click that OPENED the popover
    // isn't caught by the outside-click handler.
    const t = setTimeout(() => document.addEventListener("mousedown", onClick), 0);
    return () => {
      clearTimeout(t);
      document.removeEventListener("keydown", onKey);
      document.removeEventListener("mousedown", onClick);
    };
  }, [props]);

  if (!props.open) return null;

  return (
    <div
      ref={popRef}
      className="absolute right-0 top-12 z-50 w-80 rounded-xl border bg-card p-4 shadow-lg"
      role="dialog"
      aria-label="Portal preferences"
    >
      <div className="mb-4 flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Settings2 className="h-4 w-4 text-muted-foreground" />
          <h3 className="text-sm font-semibold">Preferences</h3>
        </div>
        <button
          type="button"
          onClick={props.onClose}
          aria-label="Close"
          className="rounded-md p-1 text-muted-foreground hover:bg-muted"
        >
          <X className="h-4 w-4" />
        </button>
      </div>

      <section className="mb-4" aria-labelledby="prefs-theme-label">
        <div
          id="prefs-theme-label"
          className="mb-2 block text-xs font-medium text-muted-foreground"
        >
          Theme
        </div>
        <div
          role="radiogroup"
          aria-labelledby="prefs-theme-label"
          className="grid grid-cols-3 gap-1 rounded-md border bg-muted/30 p-1"
        >
          <ThemeOption
            value="light"
            current={preferences.theme}
            onChange={(v) => setPreferences({ theme: v })}
            label="Light"
            icon={<Sun className="h-4 w-4" />}
          />
          <ThemeOption
            value="dark"
            current={preferences.theme}
            onChange={(v) => setPreferences({ theme: v })}
            label="Dark"
            icon={<Moon className="h-4 w-4" />}
          />
          <ThemeOption
            value="system"
            current={preferences.theme}
            onChange={(v) => setPreferences({ theme: v })}
            label="System"
            icon={<Monitor className="h-4 w-4" />}
          />
        </div>
      </section>

      <section className="mb-4" aria-labelledby="prefs-density-label">
        <div
          id="prefs-density-label"
          className="mb-2 block text-xs font-medium text-muted-foreground"
        >
          Density
        </div>
        <div
          role="radiogroup"
          aria-labelledby="prefs-density-label"
          className="grid grid-cols-2 gap-1 rounded-md border bg-muted/30 p-1"
        >
          <DensityOption
            value="comfortable"
            current={preferences.density}
            onChange={(v) => setPreferences({ density: v })}
            label="Comfortable"
          />
          <DensityOption
            value="compact"
            current={preferences.density}
            onChange={(v) => setPreferences({ density: v })}
            label="Compact"
          />
        </div>
      </section>

      <section className="mb-4" aria-labelledby="prefs-accent-label">
        <div
          id="prefs-accent-label"
          className="mb-2 block text-xs font-medium text-muted-foreground"
        >
          Accent colour
        </div>
        <div className="flex flex-wrap gap-2">
          {ACCENT_PRESETS.map((preset) => {
            const isDefault = preset.value === "";
            const active =
              (preferences.accent ?? "") === preset.value || (isDefault && !preferences.accent);
            return (
              <button
                key={preset.name}
                type="button"
                title={preset.name}
                onClick={() => setPreferences({ accent: isDefault ? undefined : preset.value })}
                className={cn(
                  "relative h-8 w-8 rounded-full border-2 transition",
                  active ? "border-foreground" : "border-transparent hover:border-muted-foreground",
                )}
                style={{
                  background: isDefault
                    ? "linear-gradient(135deg,#f1f5f9 50%,#1e293b 50%)"
                    : `hsl(${preset.value})`,
                }}
                aria-label={`${preset.name} accent`}
                aria-pressed={active}
              >
                {active && (
                  <span className="absolute inset-0 flex items-center justify-center text-xs font-bold text-white mix-blend-difference">
                    ✓
                  </span>
                )}
              </button>
            );
          })}
        </div>
      </section>

      <section className="mb-4">
        <label className="flex cursor-pointer items-center justify-between">
          <span className="flex items-center gap-2 text-sm">
            <Zap className="h-4 w-4 text-muted-foreground" />
            Reduce motion
          </span>
          <input
            type="checkbox"
            checked={preferences.reducedMotion ?? false}
            onChange={(e) => setPreferences({ reducedMotion: e.target.checked ? true : undefined })}
            className="h-4 w-4 accent-primary"
          />
        </label>
        <p className="mt-1 text-xs text-muted-foreground">
          Minimises animations + transitions. Follows your OS preference by default.
        </p>
      </section>

      <button
        type="button"
        onClick={resetPreferences}
        className="w-full rounded-md border bg-background px-3 py-1.5 text-xs text-muted-foreground hover:bg-muted"
      >
        Reset to defaults
      </button>

      <p className="mt-3 text-center text-[10px] text-muted-foreground">
        Stored in this browser only. Press{" "}
        <kbd className="rounded border bg-muted px-1 font-mono">⌘,</kbd> to toggle.
      </p>
    </div>
  );
}

function ThemeOption(props: {
  value: Theme;
  current: Theme;
  onChange: (v: Theme) => void;
  label: string;
  icon: React.ReactNode;
}): JSX.Element {
  const active = props.current === props.value;
  return (
    <button
      type="button"
      onClick={() => props.onChange(props.value)}
      className={cn(
        "flex flex-col items-center gap-1 rounded-sm px-2 py-2 text-xs transition",
        active
          ? "bg-background text-foreground shadow-sm"
          : "text-muted-foreground hover:text-foreground",
      )}
      aria-pressed={active}
    >
      {props.icon}
      <span>{props.label}</span>
    </button>
  );
}

function DensityOption(props: {
  value: Density;
  current: Density;
  onChange: (v: Density) => void;
  label: string;
}): JSX.Element {
  const active = props.current === props.value;
  return (
    <button
      type="button"
      onClick={() => props.onChange(props.value)}
      className={cn(
        "rounded-sm px-3 py-1.5 text-xs transition",
        active
          ? "bg-background text-foreground shadow-sm"
          : "text-muted-foreground hover:text-foreground",
      )}
      aria-pressed={active}
    >
      {props.label}
    </button>
  );
}
